// src/views/Settings/user/UserSettingsPanels.jsx
//
// Los paneles de configuración del usuario. Es el antiguo `views/Profile/SettingsView.jsx`
// parametrizado: en vez de una lista fija de tres acordeones, recibe qué paneles
// pintar, y así el mismo código sirve a Seguridad y a Preferencias sin duplicar
// ni un handler.
//
// Se parametrizó en vez de partirse en dos componentes porque el estado es
// compartido de verdad (el mismo `sessionId`, los mismos mensajes de guardado) y
// separarlo habría significado copiar la lógica de mutaciones dos veces.
//
// El borrado de cuenta vivía dentro del panel de privacidad, que es donde nadie
// lo busca: borrar tu usuario no es una preferencia de privacidad, es seguridad.
// Ahora es su propio panel y vive en Seguridad.
import React, { useState, useContext, useEffect } from 'react';
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import AuthContext from "../../../AuthContext";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Skeleton } from '../../../components/ui/Skeleton';
import AuthService from '../../../services/auth.service';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { readDefaultContext, writeDefaultContext } from '../../../lib/defaultContext';
import { PRIMARY, BUTTON_SIZE } from '../../../lib/buttonStyles';

// GraphQL mutation to update user privacy preferences
const UPDATE_PRIVACY_PREFERENCES = gql`
  mutation UpdatePrivacyPreferences(
    $sessionId: String!,
    $privacyStatus: String!,
    $storeData: Boolean!,
    $allowModel: Boolean!
  ) {
    updatePrivacyPreferences(
      sessionId: $sessionId,
      privacyStatus: $privacyStatus,
      storeData: $storeData,
      allowModel: $allowModel
    ) {
      success
      message
    }
  }
`;

// GraphQL query to get user privacy preferences
const GET_PRIVACY_PREFERENCES = gql`
  query GetPrivacyPreferences($sessionId: String!) {
    getPrivacyPreferences(sessionId: $sessionId) {
      privacyStatus
      storeData
      allowModel
    }
  }
`;

// GraphQL mutation to delete account
const DELETE_ACCOUNT_MUTATION = gql`
  mutation DeleteAccount($sessionId: String!, $password: String!) {
    deleteAccount(sessionId: $sessionId, password: $password) {
      success
      message
    }
  }
`;

/**
 * @param {string[]} panels  ids a pintar, en orden: privacy · password · delete · language · context
 * @param {string}  [open]   panel abierto al entrar; por defecto, el primero
 */
export default function UserSettingsPanels({ panels }) {
  const { t, i18n } = useTranslation();
  const { sessionId, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // State management
  const [privacyStatus, setPrivacyStatus] = useState('private');
  const [storeData, setStoreData] = useState(false);
  const [allowModel, setAllowModel] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Delete account state
  const [deleteStage, setDeleteStage] = useState(0); // 0: not shown, 1: initial warning, 2: final confirmation
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Status messages states
  const [privacySaved, setPrivacySaved] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [languageSaved, setLanguageSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [loadingPrivacySettings, setLoadingPrivacySettings] = useState(true);
  const [privacyError, setPrivacyError] = useState('');

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' }
  ];

  const { organizations = [], personalAccount } = useOrganization();
  const [defaultContext, setDefaultContext] = useState(() => readDefaultContext());
  const [defaultContextSaved, setDefaultContextSaved] = useState(false);

  // El catálogo completo. Cada sección pide los suyos por id, así que añadir un
  // panel es una entrada aquí y un bloque en el render.
  const ALL_SETTINGS = [
    { id: 'privacy', title: t('settings.privacy.title') },
    { id: 'password', title: t('settings.password.title') },
    { id: 'delete', title: t('settings.deleteAccount.title', 'Delete account') },
    { id: 'language', title: t('settings.language.title') },
    { id: 'context', title: t('settings.defaultContext.title', 'Default workspace') },
  ];
  const settings = ALL_SETTINGS.filter((s) => panels.includes(s.id));

  const handleDefaultContextChange = (value) => {
    const next = value === 'PERSONAL'
      ? { type: 'PERSONAL', id: personalAccount?.id || null, name: 'Personal' }
      : {
          type: 'ORGANIZATION',
          id: value,
          name: organizations.find((o) => o.id === value)?.name || null,
        };
    setDefaultContext(next);
    writeDefaultContext(next);
    setDefaultContextSaved(true);
  };

  // Query to get user privacy preferences
  const { data: privacyData } = useQuery(GET_PRIVACY_PREFERENCES, {
    variables: { sessionId },
    fetchPolicy: 'network-only',
    skip: !sessionId,
    onCompleted: (data) => {
      if (data && data.getPrivacyPreferences) {
        setPrivacyStatus(data.getPrivacyPreferences.privacyStatus || 'private');
        setStoreData(data.getPrivacyPreferences.storeData || false);
        setAllowModel(data.getPrivacyPreferences.allowModel || false);
      }
      setLoadingPrivacySettings(false);
    },
    onError: () => {
      setLoadingPrivacySettings(false);
    }
  });

  // Mutation to update privacy preferences
  const [updatePrivacyPreferences, { loading: updatingPrivacy }] = useMutation(
    UPDATE_PRIVACY_PREFERENCES,
    {
      onError: (error) => {
        setPrivacyError(error.message || "Error updating privacy settings");
      }
    }
  );

  const [changingPassword, setChangingPassword] = useState(false);

  // Mutation to delete account
  const [deleteAccount, { loading: deletingAccount }] = useMutation(
    DELETE_ACCOUNT_MUTATION,
    {
      onError: (error) => {
        setDeleteError(error.message || "Network error while deleting account");
      }
    }
  );

  // Load initial privacy settings
  useEffect(() => {
    if (privacyData && privacyData.getPrivacyPreferences) {
      const { privacyStatus: status, storeData: store, allowModel: allow } = privacyData.getPrivacyPreferences;
      setPrivacyStatus(status);
      setStoreData(store);
      setAllowModel(allow);
    }
  }, [privacyData]);

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
    setPasswordError('');
  };

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
  };

  // Autoguardado: recibe el valor que acaba de cambiar. No se puede leer del
  // estado porque `setState` no ha corrido aún cuando el `onChange` dispara
  // esto — se guardaría el valor anterior.
  const handlePrivacySave = async (overrides = {}) => {
    try {
      // Clear previous errors
      setPrivacyError('');

      // Validate sessionId exists and is not empty
      if (!sessionId || sessionId.trim() === '') {
        setPrivacyError('Missing session ID. Please log in again.');
        return;
      }

      const next = {
        privacyStatus,
        storeData: Boolean(storeData),
        allowModel: Boolean(allowModel),
        ...overrides,
      };

      // Execute the mutation with properly formatted variables
      const { data } = await updatePrivacyPreferences({
        variables: {
          sessionId,
          privacyStatus: next.privacyStatus,
          storeData: Boolean(next.storeData),
          allowModel: Boolean(next.allowModel)
        },
        context: {
          // This helps ensure Apollo sends the data in the expected format
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });
  
      // Verify the response
      if (data?.updatePrivacyPreferences) {
        if (data.updatePrivacyPreferences.success) {
          setPrivacySaved(true);
          setTimeout(() => setPrivacySaved(false), 3000);
        } else {
          setPrivacyError(data.updatePrivacyPreferences.message || 'Error updating preferences');
        }
      } else {
        setPrivacyError('Unexpected server response');
      }
    } catch (error) {
      setPrivacyError('Error saving preferences. Please try again.');
    }
  };

  const handleLanguageSave = () => {
    setLanguageSaved(true);
    setTimeout(() => setLanguageSaved(false), 3000);
  };

  // Logic to change password
  const handlePasswordSave = async () => {
    // Clear previous errors
    setPasswordError('');

    // Validate input
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError("All password fields are required");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New password and confirmation do not match");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await AuthService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      setPasswordSaved(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setPasswordSaved(false), 3000);

    } catch (error) {
      setPasswordError(error.message || "An error occurred");
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle deleting account - first confirmation
  const handleInitiateDelete = () => {
    setDeleteStage(1);
    setDeleteError('');
  };

  // Move to final delete stage
  const handleMoveToFinalDelete = () => {
    setDeleteStage(2);
    setDeleteError('');
  };

  // Cancel delete process
  const handleCancelDelete = () => {
    setDeleteStage(0);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  // Handle final account deletion
  const handleDeleteAccount = async () => {
    // Reset previous error
    setDeleteError('');

    // Validate confirmation text
    if (deleteConfirmText !== "DELETE") {
      setDeleteError("Please type DELETE to confirm");
      return;
    }

    try {
      const { data } = await deleteAccount({
        variables: {
          sessionId,
          password: "not-used" // We're not verifying password in backend anymore
        },
        context: {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });

      if (!data?.deleteAccount) {
        setDeleteError("No response from server");
        return;
      }

      if (!data.deleteAccount.success) {
        setDeleteError(data.deleteAccount.message || "Could not delete account");
        return;
      }

      // On successful deletion, log out and redirect to home page
      logout();
      navigate('/');
      
    } catch (error) {
      setDeleteError(error.message || "An error occurred while deleting account");
    }
  };

  // ── Piezas de presentación ────────────────────────────────────────────────
  // Los paneles venían con su propio CSS: radios de 32 a 40 px, padding de 50,
  // todo centrado y un lápiz de emoji como control de idioma. Al lado del resto
  // de la app parecían de otro producto. Aquí usan sus mismas piezas.
  // Sin tarjeta: los campos y los controles ya traen su borde, así que
  // envolverlos en una caja deja un marco dentro de otro.
  const CARD = '';
  const FIELD =
    'w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-white ' +
    'placeholder:text-white/35 focus:border-violet-500/60 focus:outline-none disabled:opacity-50';

  const Segmented = ({ value, onChange, options }) => (
    <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.04] p-1">
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={
            'rounded-lg px-4 py-1.5 text-sm font-medium transition ' +
            (value === option.value
              ? 'bg-primary text-white'
              : 'text-white/60 hover:text-white')
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const Saved = ({ children }) => (
    <p className="mt-3 flex items-center gap-2 text-sm text-emerald-400">
      <span aria-hidden="true">✓</span>
      {children}
    </p>
  );

  const Row = ({ title, hint, children }) => (
    <div className="space-y-2">
      <p className="text-sm font-medium text-white">{title}</p>
      {hint && <p className="text-sm text-white/50">{hint}</p>}
      {children}
    </div>
  );

  const yesNo = [
    { value: true, label: t('general.yes', 'Yes') },
    { value: false, label: t('general.no', 'No') },
  ];

  return (
    <div className="max-w-3xl space-y-10">
      {settings.map((setting) => (
        // Plano, con un encabezado por sección. Eran acordeones: para cambiar la
        // contraseña había que adivinar que estaba detrás de una fila plegada.
        <section key={setting.id} className="space-y-4">
          <h2 className="text-lg font-semibold text-white">{setting.title}</h2>

          {setting.id === 'privacy' && (
            <div className={CARD}>
              {/* Un esqueleto con la forma que va a tener, como en Perfil. Un
                  spinner centrado no dice qué va a aparecer y mueve la pantalla
                  al llegar los datos, porque ocupa otro alto que el contenido. */}
              {loadingPrivacySettings ? (
                <div className="space-y-6">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton height="16px" width="180px" rounded="sm" />
                      <Skeleton height="14px" width="320px" rounded="sm" />
                      <Skeleton height="38px" width="160px" rounded="xl" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  <Row
                    title={t('settings.privacy.accountVisibilityTitle')}
                    hint={t('settings.privacy.accountVisibilityDescription')}
                  >
                    <Segmented
                      value={privacyStatus}
                      onChange={(value) => {
                        setPrivacyStatus(value);
                        handlePrivacySave({ privacyStatus: value });
                      }}
                      options={[
                        { value: 'public', label: t('settings.privacy.public') },
                        { value: 'private', label: t('settings.privacy.private') },
                      ]}
                    />
                  </Row>

                  <Row
                    title={t('settings.privacy.storeDataTitle')}
                    hint={t('settings.privacy.storeDataDescription')}
                  >
                    <Segmented
                      value={storeData}
                      onChange={(value) => {
                        setStoreData(value);
                        handlePrivacySave({ storeData: value });
                      }}
                      options={yesNo}
                    />
                  </Row>

                  <Row
                    title={t('settings.privacy.allowModelTitle')}
                    hint={t('settings.privacy.allowModelDescription')}
                  >
                    <Segmented
                      value={allowModel}
                      onChange={(value) => {
                        setAllowModel(value);
                        handlePrivacySave({ allowModel: value });
                      }}
                      options={yesNo}
                    />
                  </Row>

                  {privacyError && (
                    <p className="text-sm text-red-400">{privacyError}</p>
                  )}

                  {/* Sin botón: cada control guarda al cambiar. Un "Guardar" para
                      tres interruptores obliga a un paso extra que no decide
                      nada, y deja al usuario sin saber si lo que ve está
                      guardado o pendiente. */}
                  {updatingPrivacy && (
                    <p className="text-sm text-white/40">
                      {t('profilePage.modal.saving', 'Saving…')}
                    </p>
                  )}
                  {privacySaved && <Saved>{t('settings.privacy.savedMessage')}</Saved>}
                </div>
              )}
            </div>
          )}

          {setting.id === 'password' && (
            <div className={CARD}>
              <div className="space-y-4">
                <p className="text-sm text-white/50">{t('settings.password.requirements')}</p>

                <div className="space-y-3">
                  <input
                    type="password"
                    name="currentPassword"
                    className={FIELD}
                    placeholder={t('settings.password.currentPassword')}
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    disabled={changingPassword}
                  />
                  <input
                    type="password"
                    name="newPassword"
                    className={FIELD}
                    placeholder={t('settings.password.newPassword')}
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    disabled={changingPassword}
                  />
                  <input
                    type="password"
                    name="confirmPassword"
                    className={FIELD}
                    placeholder={t('settings.password.retypePassword')}
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    disabled={changingPassword}
                  />
                </div>

                {passwordError && <p className="text-sm text-red-400">{passwordError}</p>}

                <NavLink
                  to="/ForgotPassword"
                  className="inline-block text-sm text-violet-300 hover:text-violet-200"
                >
                  {t('settings.password.forgot')}
                </NavLink>

                <div>
                  <button
                    type="button"
                    onClick={handlePasswordSave}
                    disabled={changingPassword}
                    className={`${PRIMARY} disabled:opacity-50`}
                  >
                    {changingPassword ? t('profilePage.modal.saving', 'Saving…') : t('settings.password.change')}
                  </button>
                  {passwordSaved && <Saved>{t('settings.password.savedMessage')}</Saved>}
                </div>
              </div>
            </div>
          )}

          {setting.id === 'delete' && (
            <div className={CARD}>
              <p className="text-sm text-white/50">{t('settings.privacy.deleteDataDescription')}</p>

              {deleteStage === 0 && (
                <button
                  type="button"
                  onClick={handleInitiateDelete}
                  className={`${BUTTON_SIZE} mt-4 bg-red-600 text-white hover:bg-red-700`}
                >
                  {t('settings.privacy.deleteDataButton')}
                </button>
              )}

              {deleteStage > 0 && (
                <div className="mt-4 space-y-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                  <p className="flex items-center gap-2 font-medium text-red-300">
                    <span aria-hidden="true">⚠️</span>
                    {deleteStage === 1
                      ? t('settings.privacy.warningTitle')
                      : t('settings.privacy.finalWarningTitle')}
                  </p>
                  <p className="text-sm text-white/60">
                    {deleteStage === 1
                      ? t('settings.privacy.warningDescription')
                      : t('settings.privacy.finalWarningDescription')}
                  </p>

                  {deleteStage === 2 && (
                    <div className="space-y-2">
                      <label htmlFor="delete-confirm-input" className="block text-sm text-white/70">
                        {t('settings.privacy.typeToConfirm', { text: 'DELETE' })}
                      </label>
                      <input
                        id="delete-confirm-input"
                        type="text"
                        className={FIELD}
                        placeholder="DELETE"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                      />
                    </div>
                  )}

                  {deleteError && <p className="text-sm text-red-400">{deleteError}</p>}

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleCancelDelete}
                      className={`${BUTTON_SIZE} border border-white/15 bg-white/5 text-white hover:bg-white/10`}
                    >
                      {t('settings.privacy.cancel')}
                    </button>
                    {deleteStage === 1 ? (
                      <button
                        type="button"
                        onClick={handleMoveToFinalDelete}
                        className={`${BUTTON_SIZE} bg-red-600 text-white hover:bg-red-700`}
                      >
                        {t('settings.privacy.proceed')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                        className={`${BUTTON_SIZE} bg-red-600 text-white hover:bg-red-700 disabled:opacity-40`}
                      >
                        {deletingAccount
                          ? t('settings.privacy.deleting', 'Deleting…')
                          : t('settings.privacy.confirmDelete', 'Delete My Account')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {setting.id === 'language' && (
            <div className={CARD}>
              <Row title={t('settings.language.preferred')}>
                {/* Era un texto con un lápiz de emoji que abría una lista a mano.
                    Un `select` hace lo mismo, se opera con el teclado y se ve
                    como los demás campos. */}
                <select
                  className={FIELD}
                  value={i18n.language}
                  onChange={(e) => {
                    handleLanguageChange(e.target.value);
                    handleLanguageSave();
                  }}
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </Row>
              {languageSaved && <Saved>{t('settings.language.savedMessage')}</Saved>}
            </div>
          )}

          {/* DEFAULT CONTEXT — el "Default Team" de Vercel: con ocho contextos en
              el switcher, aterrizar en el que toque deja de ser aceptable. */}
          {setting.id === 'context' && (
            <div className={CARD}>
              <Row title={t('settings.defaultContext.description', 'Where you land when you sign in')}>
                <select
                  className={FIELD}
                  value={defaultContext?.type === 'ORGANIZATION' ? defaultContext.id : 'PERSONAL'}
                  onChange={(e) => handleDefaultContextChange(e.target.value)}
                >
                  <option value="PERSONAL">{t('sidebar.personal', 'Personal')}</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </Row>
              {defaultContextSaved && <Saved>{t('settings.defaultContext.savedMessage', 'Saved')}</Saved>}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
